import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "components/ui/dialog";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Loader, ShoppingCart } from "lucide-react";
import { useAuth } from "AuthContext";
import { createTiOrder } from "actions/tiAction";
import { saveOrder } from "actions/cartActions";
import { warnIfHubSyncFailed } from "utils/hubSyncWarning";
import { getProductPrice, formatPrice } from "utils/cartUtils";
import { getCurrencySymbol } from "utils/searchUtils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const TIModal = ({ open, modelData, setModelData }) => {
  const navigate = useNavigate();
  const { cart, removeByPartNumber } = useAuth();
  const tiCartItems = useMemo(
    () => cart.filter((item) => item.api_partner?.toLowerCase() === "ti"),
    [cart],
  );

  const [poNumber, setPoNumber] = useState(`TI-${Date.now()}`);
  const [comments, setComments] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Holds the TI response once an order has actually been placed. Recording it
  // in My Orders can still fail, and a retry must re-run only that step: the
  // order already exists at TI, and placing a second one is far worse than a
  // missing row. Cleared when the modal closes.
  const placedOrderRef = useRef(null);

  const currencySymbol = getCurrencySymbol(tiCartItems[0]?.price?.currency);
  const totalUnits = tiCartItems.reduce((a, it) => a + (parseInt(it.required_qty, 10) || 0), 0);
  const grandTotal = tiCartItems.reduce(
    (a, it) => a + (parseInt(it.required_qty, 10) || 0) * getProductPrice(it),
    0,
  );

  const handleClose = () => {
    setModelData({ ...modelData, open: false });
    setError(null);
    placedOrderRef.current = null;
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (tiCartItems.length === 0) {
        setError("No TI items in the cart.");
        setIsLoading(false);
        return;
      }

      // Only place the order if a previous attempt has not already done so.
      let placed = placedOrderRef.current;

      if (!placed) {
        const res = await createTiOrder({
          po_number: poNumber.trim(),
          comments: comments.trim(),
          items: tiCartItems.map((item) => ({
            tiPartNumber: item.part_number,
            quantity: parseInt(item.required_qty, 10) || 0,
          })),
        });

        if (!res?.status) {
          setError(res?.message || "Failed to place the TI order.");
          setIsLoading(false);
          return;
        }

        // The test environment validates and prices without placing anything,
        // and the order number it echoes cannot be retrieved later. Show the
        // result, but do not file it in My Orders or empty the cart.
        if (res.data?.is_test) {
          setIsLoading(false);
          await MySwal.fire({
            title: <strong>Test order validated</strong>,
            html: (
              <i>
                {res.message ||
                  "Validated against the TI test environment. Nothing was placed."}
              </i>
            ),
            icon: "info",
          });
          return;
        }

        placed = res.data;
        placedOrderRef.current = placed;
      }

      // Record the order in "My Orders" (ti_data + order_items, distributor=TI).
      const bomLinks = tiCartItems
        .filter((item) => item.bom_item_id)
        .map((item) => ({
          bom_item_id: item.bom_item_id,
          bom_id: item.bom_id ?? null,
          mfr_part_number: item.part_number ?? null,
          part_number: item.api_partner_part_number ?? null,
        }));

      const saveOrderResponse = await saveOrder({
        ti_data: {
          ...placed,
          cartDetailItems: tiCartItems.map((item) => ({
            ...item,
            unit_price: getProductPrice(item),
          })),
        },
        bom_links: bomLinks,
      });
      warnIfHubSyncFailed(saveOrderResponse?.data?.hub_sync);

      handleClose();
      tiCartItems.forEach((item) => removeByPartNumber(item.part_number));

      await MySwal.fire({
        title: <strong>TI Order Placed !</strong>,
        html: (
          <i>
            TI order number <b>{placed?.orderNumber}</b>
            {placed?.orderStatus ? ` (${placed.orderStatus})` : ""}.
          </i>
        ),
        icon: "success",
      });
      navigate("/orders");
    } catch (e) {
      // Past this point the order exists at TI, so say so rather than inviting
      // a resubmit that would place a second one. Retrying now only re-attempts
      // the My Orders write, because placedOrderRef short-circuits the order.
      if (placedOrderRef.current) {
        setError(
          `Your TI order ${placedOrderRef.current?.orderNumber || ""} was placed, but saving it to My Orders failed: ${e.message}. ` +
            "Press Place Order again to retry saving only - it will not order a second time.",
        );
      } else {
        setError(e.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-primary text-lg font-semibold">Place TI Order</DialogTitle>
          <DialogDescription>
            Shipping, billing and payment come from the checkout profile saved on your TI account -
            only the parts and quantities below are sent.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* PO number */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tiPoNumber">Purchase Order Number</Label>
            <input
              id="tiPoNumber"
              type="text"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#5F47C9]/20"
            />
          </div>

          {/* Comments */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tiComments">Order Comments</Label>
            <textarea
              id="tiComments"
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Optional note sent with the order"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#5F47C9]/20"
            />
          </div>

          {/* Line-item preview */}
          <div className="border border-[#BCD8D4] rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-[#E0FCF8]">
              <span className="text-[12px] font-semibold text-[#310086]">
                Order lines ({tiCartItems.length} parts · {totalUnits} units)
              </span>
            </div>
            <div className="overflow-x-auto max-h-52 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-[0.06em] text-[#4E6763]">
                    <th className="px-3 py-2 text-left">TI Part Number</th>
                    <th className="px-3 py-2 text-center">Qty</th>
                    <th className="px-3 py-2 text-right">Unit Price</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {tiCartItems.map((item, i) => {
                    const unit = getProductPrice(item);
                    const qty = parseInt(item.required_qty, 10) || 0;
                    return (
                      <tr key={i} className="border-t border-[#E0FCF8]">
                        <td className="px-3 py-2 font-mono text-[13px]">{item.part_number}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{qty}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {currencySymbol} {unit}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {currencySymbol} {formatPrice(qty * unit)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-[#BCD8D4] bg-[#FBFAFF]">
                    <td className="px-3 py-2 font-semibold" colSpan={3}>
                      Estimated total
                    </td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">
                      {currencySymbol} {formatPrice(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="px-3 py-2 text-[11px] text-[#4E6763] border-t border-[#E0FCF8]">
              TI prices and taxes are confirmed by TI when the order is accepted; the total above is
              an estimate from the search results.
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-500">
              Error: <span className="text-red-500">{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="hover:bg-gray-300 border border-gray-300"
            type="button"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button variant="primary" type="button" disabled={isLoading} onClick={handleSubmit}>
            {isLoading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <ShoppingCart className="w-4 h-4" />
            )}
            Place Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TIModal;
